/** Limite de anexos por envio (fila de preview). */
export const MAX_CHAT_ATTACHMENTS = 20

/** Limite para imagens (JPG, PNG, GIF, WebP). */
export const MAX_CHAT_IMAGE_BYTES = 5 * 1024 * 1024

/** Limite para demais arquivos permitidos. */
export const MAX_CHAT_FILE_BYTES = 10 * 1024 * 1024

/** Limite para mensagens de áudio (gravação ou arquivo). */
export const MAX_CHAT_AUDIO_BYTES = 5 * 1024 * 1024

/** MIME padrão após conversão cross-platform (iOS/Android/web). */
export const CHAT_AUDIO_SEND_MIME = 'audio/mpeg'

/** Limite de caracteres por mensagem de texto no chat. */
export const MAX_CHAT_MESSAGE_CHARS = 250

function formatSizeForError(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${bytes} B`
}

const EXT_TO_MIME: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.webm': 'audio/webm',
  '.m4a': 'audio/mp4',
  '.mp3': 'audio/mpeg',
  '.ogg': 'audio/ogg',
  '.wav': 'audio/wav',
  '.aac': 'audio/aac',
}

const AUDIO_TYPES = new Set([
  'audio/webm',
  'audio/mp4',
  'audio/mpeg',
  'audio/ogg',
  'audio/wav',
  'audio/x-m4a',
  'audio/aac',
  'audio/m4a',
])

export function isChatAudioMime(mime: string): boolean {
  const t = mime.trim().toLowerCase()
  if (!t) return false
  if (AUDIO_TYPES.has(t)) return true
  return t.startsWith('audio/')
}

export function looksLikeChatAudioAttachment(
  fileUrl: string | undefined,
  fileName: string | undefined,
  contentType: string | undefined
): boolean {
  if (contentType && isChatAudioMime(contentType)) {
    return true
  }
  const n = (fileName || '').toLowerCase()
  if (/\.(webm|m4a|mp3|ogg|wav|aac)$/i.test(n)) {
    return true
  }
  if (/\.mp4$/i.test(n) && /audio/i.test(n)) {
    return true
  }
  if (fileUrl && /\.(webm|m4a|mp3|ogg|wav|aac)(\?|#|$)/i.test(fileUrl)) {
    return true
  }
  return false
}

export function buildRecordedAudioFilename(mime: string): string {
  const m = mime.toLowerCase()
  if (m.includes('mpeg') || m.includes('mp3')) {
    return `audio-${Date.now()}.mp3`
  }
  if (m.includes('mp4') || m.includes('m4a')) {
    return `audio-${Date.now()}.m4a`
  }
  if (m.includes('ogg')) {
    return `audio-${Date.now()}.ogg`
  }
  return `audio-${Date.now()}.webm`
}

/** MIME efetivo (alguns browsers/OS deixam `file.type` vazio). */
export function effectiveFileMime(file: File): string {
  const raw = file.type?.trim()
  if (raw) return raw
  const name = (file.name || '').toLowerCase()
  const dot = name.lastIndexOf('.')
  if (dot < 0) return ''
  return EXT_TO_MIME[name.slice(dot)] ?? ''
}

export function getChatAttachmentAcceptAttr(): string {
  return '*/*'
}

export function validateChatAttachment(file: File): string | null {
  const t = effectiveFileMime(file) || 'application/octet-stream'

  if (t.startsWith('image/')) {
    if (file.size > MAX_CHAT_IMAGE_BYTES) {
      return `Imagem maior que o permitido (${formatSizeForError(
        file.size
      )}). Máximo: 5 MB.`
    }
    return null
  }

  if (isChatAudioMime(t)) {
    if (file.size > MAX_CHAT_AUDIO_BYTES) {
      return `Áudio maior que o permitido (${formatSizeForError(
        file.size
      )}). Máximo: 5 MB.`
    }
    return null
  }

  if (file.size > MAX_CHAT_FILE_BYTES) {
    return `Arquivo maior que o permitido (${formatSizeForError(
      file.size
    )}). Máximo: 10 MB.`
  }
  return null
}

export function mergePendingChatAttachments(
  current: readonly File[],
  incoming: readonly File[]
): { next: File[]; rejected: { name: string; reason: string }[] } {
  const next = [...current]
  const rejected: { name: string; reason: string }[] = []

  for (const file of incoming) {
    if (next.length >= MAX_CHAT_ATTACHMENTS) {
      rejected.push({
        name: file.name || 'Arquivo',
        reason: `Máximo de ${MAX_CHAT_ATTACHMENTS} anexos por envio.`,
      })
      continue
    }
    const err = validateChatAttachment(file)
    if (err) {
      rejected.push({ name: file.name || 'Arquivo', reason: err })
      continue
    }
    next.push(file)
  }

  return { next, rejected }
}

export function isChatImageFile(file: File): boolean {
  const mime = effectiveFileMime(file)
  return mime.startsWith('image/')
}
